/**
 * Bostadshyresavtal — Version 2026 (Formulär R-01), IR twin of the legacy
 * `bostadshyra/v1` template.
 *
 * Sections number themselves (`§ N`) among the visible ones, so the
 * conditional Förhandlingsklausul leaves no gap. After every declared
 * section sits a custom-sections anchor, which is where the legacy lane
 * renders a contract's own sections (ADR-0060); unanchored customs land
 * before the signature section, as before.
 */
import { applyDefaults, COMPOSITION_SCHEMA, COMPOSITION_VERSION } from '@hyranai/regta-document-ir';
import { BOSTADSHYRA_BLOCKS } from '../bostadshyra/blocks.js';
import { optionOf } from '../bostadshyra/kit.js';
import { RENT_MODEL_OPTIONS } from '../bostadshyra/options.js';
import { declaredSectionInstances } from '../common/custom-sections.js';
import { whenOption } from '../common/slots.js';
const TITLE = 'Bostadshyresavtal - Version 2026';
const FORM_NUMBER = 'Formulär R-01';
const PREAMBLE = 'Undertecknande parter har denna dag träffat följande hyresavtal.';
const STATIC_CLAUSES = [
    { instanceId: 'hinder-och-men', blockRef: 'bostadshyra.hinder-och-men@v1', input: {} },
    { instanceId: 'hyresgastens-ataganden', blockRef: 'bostadshyra.hyresgastens-ataganden@v1', input: {} },
    { instanceId: 'rokforbud', blockRef: 'bostadshyra.rokforbud@v1', input: {} },
    { instanceId: 'force-majeure', blockRef: 'bostadshyra.force-majeure@v1', input: {} },
    { instanceId: 'tidigare-hyresavtal', blockRef: 'bostadshyra.tidigare-hyresavtal@v1', input: {} },
];
const SECTIONS = [
    { ref: 'hyresvard', blockRef: 'bostadshyra.hyresvard@v1' },
    { ref: 'hyresgast', blockRef: 'bostadshyra.hyresgast@v1', besidePrevious: true },
    { ref: 'lagenhets-adress', blockRef: 'bostadshyra.lagenhets-adress@v1' },
    { ref: 'anvandning', blockRef: 'bostadshyra.anvandning@v1' },
    { ref: 'storlek', blockRef: 'bostadshyra.storlek@v1', besidePrevious: true },
    { ref: 'tillbehor', blockRef: 'bostadshyra.tillbehor@v1' },
    { ref: 'hyrestid', blockRef: 'bostadshyra.hyrestid@v1' },
    { ref: 'uppsagning', blockRef: 'bostadshyra.uppsagning@v1' },
    { ref: 'hyressattningsmodell', blockRef: 'bostadshyra.hyressattningsmodell@v1' },
    {
        ref: 'forhandlingsklausul',
        blockRef: 'bostadshyra.forhandlingsklausul@v1',
        includeWhen: whenOption('rent_model', optionOf(RENT_MODEL_OPTIONS, 'kollektiv')),
    },
    { ref: 'hyra', blockRef: 'bostadshyra.hyra@v1' },
    { ref: 'tillagg', blockRef: 'bostadshyra.tillagg@v1' },
    { ref: 'betalning', blockRef: 'bostadshyra.betalning@v1' },
    { ref: 'deposition', blockRef: 'bostadshyra.deposition@v1' },
    { ref: 'garage', blockRef: 'bostadshyra.garage@v1' },
    { ref: 'reservnycklar', blockRef: 'bostadshyra.reservnycklar@v1' },
    { ref: 'sarskilda-bestammelser', blockRef: 'bostadshyra.sarskilda-bestammelser@v1' },
    {
        ref: 'underskrift',
        blockRef: 'bostadshyra.underskrifter@v1',
        before: STATIC_CLAUSES,
        input: {
            statement: true,
            parties: [
                { label: 'Hyresvärd', role: 'lessor', ordinal: 0 },
                { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                { label: 'Medhyresgäst', role: 'lessee', ordinal: 1, when_set: 'cotenant_name' },
                { label: 'Borgensman', role: 'guarantor', ordinal: 0, when_set: 'has_guarantor' },
            ],
        },
    },
];
/** The legacy create-time defaults, read off the slots' own `default` (option codes for selects). */
export const BOSTADSHYRA_DEFAULTS = applyDefaults({
    fields: BOSTADSHYRA_BLOCKS.flatMap((block) => block.slots),
});
export const BOSTADSHYRA_SECTION_REFS = SECTIONS.map((s) => s.ref);
export const bostadshyraV1 = (values = {}) => ({
    schema: COMPOSITION_SCHEMA,
    version: COMPOSITION_VERSION,
    documentKey: 'bostadshyra',
    documentVersion: 'v1',
    title: TITLE,
    locale: 'sv-SE',
    meta: {
        category: 'Bostadshyra',
        numberStyle: 'paragraph',
        shortTitle: 'Bostadshyresavtal',
        formNumber: FORM_NUMBER,
        preamble: PREAMBLE,
        signingMode: 'digital',
        partySlots: [
            { id: 'lessor', label: 'Hyresvärd', role: 'lessor', ordinal: 0, required: true, signs: true },
            { id: 'lessee_primary', label: 'Hyresgäst (primär)', role: 'lessee', ordinal: 0, required: true, signs: true },
            { id: 'lessee_co', label: 'Medhyresgäst', role: 'lessee', ordinal: 1, signs: true, help: 'Valfritt — fyll i om kontraktet har en andra hyresgäst.' },
            { id: 'guarantor', label: 'Borgensman', role: 'guarantor', ordinal: 0, signs: true, help: 'Valfritt — endast om någon går i borgen för hyran.' },
        ],
        propertySlots: [{ id: 'property', label: 'Fastighet', multi: false, required: true }],
    },
    values: { ...BOSTADSHYRA_DEFAULTS, ...values },
    blocks: [
        { instanceId: 'letterhead', blockRef: 'common.letterhead@v1', slot: 'letterhead', input: { title: TITLE, form_number: FORM_NUMBER } },
        { instanceId: 'footer', blockRef: 'common.page-footer@v1', slot: 'footer', input: { form_number: FORM_NUMBER } },
        { instanceId: 'avtalsuppgifter', blockRef: 'bostadshyra.avtalsuppgifter@v1', input: {} },
        { instanceId: 'preamble', blockRef: 'common.preamble@v1', input: { text: PREAMBLE, variant: 'intro' } },
        ...declaredSectionInstances(SECTIONS, { customBlockRef: 'bostadshyra.custom-sections@v1' }),
    ],
});
//# sourceMappingURL=bostadshyra.js.map